import React, { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/date';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

export function RegisterRequests() {
  const { registerRequests = [], approveRegisterRequest, rejectRegisterRequest } = useAuthStore();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<'user' | 'admin'>('user');

  // Filter pending requests first
  const pendingRequests = registerRequests.filter(r => r.status === 'pending');
  const otherRequests = registerRequests.filter(r => r.status !== 'pending');

  const handleApprove = () => {
    if (selectedRequest) {
      approveRegisterRequest(selectedRequest.id, selectedRole);
      setSelectedRequest(null);
    }
  };

  const formatBirthDate = (birthDate: any) => {
    if (!birthDate) return 'غير محدد';
    
    // If birthDate is an object with type, year, month, day
    if (birthDate.type && birthDate.year && birthDate.month && birthDate.day) {
      return `${birthDate.day}/${birthDate.month}/${birthDate.year} ${birthDate.type === 'hijri' ? '(هجري)' : '(ميلادي)'}`;
    }
    
    // If birthDate is a string
    return birthDate;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">طلبات التسجيل</h2>

      {registerRequests.length === 0 ? (
        <p className="text-center text-gray-500 p-4 bg-white rounded-lg shadow-sm">
          لا توجد طلبات تسجيل
        </p>
      ) : (
        <div className="space-y-4">
          {/* Pending Requests */}
          {pendingRequests.map((request) => (
            <div key={request.id} className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex justify-between items-start">
                <div className="space-y-4 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{request.data.name}</h3>
                    <Badge variant="warning">قيد المراجعة</Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    {/* Personal Information */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">المعلومات الشخصية</h4>
                      <p>
                        <span className="font-medium ml-1">رقم الجوال:</span>
                        <span dir="ltr">{request.data.phoneNumber}</span>
                      </p>
                      <p>
                        <span className="font-medium ml-1">الحالة الاجتماعية:</span>
                        {request.data.maritalStatus === 'single' ? 'أعزب' :
                         request.data.maritalStatus === 'married' ? 'متزوج' :
                         request.data.maritalStatus === 'divorced' ? 'مطلق' : 'أرمل'}
                      </p>
                      <p>
                        <span className="font-medium ml-1">تاريخ الميلاد:</span>
                        {formatBirthDate(request.data.birthDate)}
                      </p>
                    </div>

                    {/* Family Information */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">معلومات العائلة</h4>
                      <p>
                        <span className="font-medium ml-1">اسم الأب:</span>
                        {request.data.fatherName}
                      </p>
                      <p>
                        <span className="font-medium ml-1">اسم الجد:</span>
                        {request.data.grandfather1}
                      </p>
                      <p>
                        <span className="font-medium ml-1">اسم الجد الثاني:</span>
                        {request.data.grandfather2}
                      </p>
                      <p>
                        <span className="font-medium ml-1">اسم الجد الثالث:</span>
                        {request.data.grandfather3}
                      </p>
                    </div>

                    {/* Additional Information */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">معلومات إضافية</h4>
                      <p>
                        <span className="font-medium ml-1">المنطقة:</span>
                        {request.data.region}
                      </p>
                      <p>
                        <span className="font-medium ml-1">المهنة:</span>
                        {request.data.occupation}
                      </p>
                      <p>
                        <span className="font-medium ml-1">الفرع:</span>
                        {request.data.branch}
                      </p>
                      <p>
                        <span className="font-medium ml-1">المرحلة الدراسية:</span>
                        {request.data.educationLevel === 'other' 
                          ? request.data.otherEducationLevel 
                          : request.data.educationLevel}
                      </p>
                    </div>

                    {/* Request Information */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">معلومات الطلب</h4>
                      <p>
                        <span className="font-medium ml-1">تاريخ الطلب:</span>
                        {formatDate(new Date(request.timestamp))}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setSelectedRequest(request)}
                    className="hover:bg-secondary"
                  >
                    قبول
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => rejectRegisterRequest(request.id)}
                  >
                    رفض
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {/* Other Requests */}
          {otherRequests.map((request) => (
            <div key={request.id} className="bg-white p-6 rounded-lg shadow-sm opacity-75">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{request.data.name}</h3>
                  <Badge 
                    variant={request.status === 'approved' ? 'success' : 'destructive'}
                  >
                    {request.status === 'approved' ? 'تمت الموافقة' : 'مرفوض'}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <p>
                    <span className="font-medium ml-1">رقم الجوال:</span>
                    <span dir="ltr">{request.data.phoneNumber}</span>
                  </p>
                  <p>
                    <span className="font-medium ml-1">تاريخ الطلب:</span>
                    {formatDate(new Date(request.timestamp))}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval Dialog */}
      {selectedRequest && (
        <Dialog open={true} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>تأكيد قبول طلب التسجيل</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>نوع العضوية</Label>
                <RadioGroup
                  value={selectedRole}
                  onValueChange={(value: 'user' | 'admin') => setSelectedRole(value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="user" id="user" />
                    <Label htmlFor="user">مستخدم عادي</Label>
                  </div>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="admin" id="admin" />
                    <Label htmlFor="admin">مدير</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            <DialogFooter className="sm:justify-start">
              <Button onClick={handleApprove}>
                تأكيد القبول
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSelectedRequest(null)}
                className="mr-2"
              >
                إلغاء
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}